import {
  type ReassignmentSchedulingTaskContext,
  type Task,
  taskReassignmentSchedulingContextOptions,
} from '@cartwheel/api/v1'
import { LoadingView } from '@cartwheel/components'
import { useQuery } from '@tanstack/react-query'
import {
  AvailabilityNote,
  CanceledNotice,
  ScheduleAppointmentsSection,
  SectionDivider,
  TaskDescription,
} from './shared/task-workspace-primitives'

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm font-semibold leading-[1.5] text-charcoal">{label}</p>
      <p className="mt-1 text-sm leading-[1.5] text-charcoal-50">{value}</p>
    </div>
  )
}

function ReassignmentDetails({ context }: { context: ReassignmentSchedulingTaskContext }) {
  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Reassignment</h3>
      <div className="space-y-4 rounded-lg border border-sand bg-white p-4 shadow-sm">
        <Detail label="Service line" value={context.service_line} />
        {context.effective_date ? (
          <Detail label="Effective date" value={context.effective_date} />
        ) : null}
      </div>
    </section>
  )
}

function FamilyPreferences({ context }: { context: ReassignmentSchedulingTaskContext }) {
  const identityPreferences = context.identity_preferences.join(', ') || 'No preference'
  const meetingPreference = context.meeting_preference || 'No preference'

  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Family preferences</h3>
      <div className="space-y-4 rounded-lg border border-sand bg-white p-4 shadow-sm">
        <Detail label="Clinician identity preferences" value={identityPreferences} />
        <Detail label="Meeting preference" value={meetingPreference} />
      </div>
    </section>
  )
}

function CompletedNotice() {
  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Completed</h3>
      <div className="rounded-lg border border-sand bg-white p-4 shadow-sm">
        <p className="text-sm leading-[1.5] text-charcoal-50">
          A replacement appointment series has been scheduled for this client.
        </p>
      </div>
    </section>
  )
}

export function ScheduleReassignmentWorkspace({
  task,
  canWork = true,
}: {
  task: Task
  canWork?: boolean
  onClose: () => void
}) {
  const contextQuery = useQuery({
    ...taskReassignmentSchedulingContextOptions({ path: { task_id: task.id } }),
    enabled: task.status === 'open',
  })

  if (task.status === 'canceled') {
    return (
      <div className="space-y-8">
        <TaskDescription task={task} />
        <CanceledNotice />
      </div>
    )
  }

  if (task.status === 'completed') {
    return (
      <div className="space-y-8">
        <TaskDescription task={task} />
        <CompletedNotice />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <TaskDescription task={task} />

      {contextQuery.isPending ? <LoadingView title="Loading reassignment details…" /> : null}

      {contextQuery.isError ? (
        <p className="text-sm leading-[1.5] text-charcoal-50">
          Reassignment details could not be loaded. Please try again.
        </p>
      ) : null}

      {contextQuery.data ? (
        <>
          <ReassignmentDetails context={contextQuery.data} />
          <FamilyPreferences context={contextQuery.data} />
          {contextQuery.data.availability_note ? (
            <AvailabilityNote note={contextQuery.data.availability_note} />
          ) : null}

          {canWork ? (
            <>
              <SectionDivider />
              <section className="space-y-4">
                <div>
                  <h3 className="text-base font-semibold leading-[1.5] text-charcoal">
                    Resolution
                  </h3>
                  <p className="text-sm leading-[1.5] text-charcoal-50">
                    Find a new clinician and schedule the replacement series
                  </p>
                </div>
                <ScheduleAppointmentsSection
                  task={task}
                  matchingCliniciansCount={contextQuery.data.matching_clinicians_count}
                  caseAssignmentUrl={contextQuery.data.case_assignment_url}
                />
              </section>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
